// Комментарии на русском: утилиты Web Push (подписка браузера на push).

import { getAccessToken } from '@/lib/authStorage';

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export async function enableWebPush(apiBase: string): Promise<{ ok: boolean; error?: string }> {
  try {
    if (typeof window === 'undefined') return { ok: false, error: 'no_window' };
    if (!('serviceWorker' in navigator)) return { ok: false, error: 'no_service_worker' };
    if (!('PushManager' in window)) return { ok: false, error: 'no_push_manager' };
    if (!('Notification' in window)) return { ok: false, error: 'no_notifications' };

    if (Notification.permission === 'default') {
      const p = await Notification.requestPermission();
      if (p !== 'granted') return { ok: false, error: 'permission_denied' };
    }
    if (Notification.permission !== 'granted') return { ok: false, error: 'permission_denied' };

    // Регистрируем SW (один раз)
    // Комментарий на русском: регистрируем SW с build-id, чтобы при деплое браузер гарантированно подтягивал новый SW.
    const buildId = process.env.NODE_ENV === 'development' ? 'dev' : String(process.env.NEXT_PUBLIC_APP_BUILD_ID || '').trim();
    // Комментарий на русском: отдельный build-id для API — чтобы можно было сбрасывать API-кэш без сброса shell/pages/asset кэшей.
    const apiBuildId = String(process.env.NEXT_PUBLIC_API_BUILD_ID || '').trim();
    const qs = [buildId ? `v=${encodeURIComponent(buildId)}` : '', apiBuildId ? `api=${encodeURIComponent(apiBuildId)}` : '']
      .filter(Boolean)
      .join('&');
    const swUrl = qs ? `/push-sw.js?${qs}` : '/push-sw.js';
    const reg = await navigator.serviceWorker.register(swUrl, { scope: '/', updateViaCache: 'none' });
    await navigator.serviceWorker.ready;

    // Берём VAPID public key с бэка
    const pkRes = await fetch(`${apiBase}/api/push/public-key/`, { method: 'GET' });
    if (!pkRes.ok) return { ok: false, error: 'no_public_key' };
    const pk = (await pkRes.json()) as any;
    const publicKey = String(pk?.publicKey || '');
    if (!publicKey) return { ok: false, error: 'no_public_key' };

    const existing = await reg.pushManager.getSubscription();
    const sub =
      existing ||
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      }));

    const token = getAccessToken() || '';
    if (!token) return { ok: false, error: 'no_auth' };

    const body = JSON.stringify({ subscription: (sub as any).toJSON ? (sub as any).toJSON() : sub });
    const res = await fetch(`${apiBase}/api/push/subscribe/`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body,
    });
    if (!res.ok) return { ok: false, error: `subscribe_failed_${res.status}` };

    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: String(e?.message || e) };
  }
}

export async function disableWebPush(apiBase: string): Promise<{ ok: boolean; error?: string }> {
  try {
    if (typeof window === 'undefined') return { ok: false, error: 'no_window' };
    if (!('serviceWorker' in navigator)) return { ok: false, error: 'no_service_worker' };

    const reg = await navigator.serviceWorker.getRegistration('/');
    if (!reg) return { ok: true };

    const sub = await reg.pushManager.getSubscription();
    if (!sub) return { ok: true };

    const endpoint = String((sub as any).endpoint || '');
    try {
      await sub.unsubscribe();
    } catch {
      // ignore
    }

    const token = getAccessToken() || '';
    if (token && endpoint) {
      await fetch(`${apiBase}/api/push/unsubscribe/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint }),
      });
    }

    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: String(e?.message || e) };
  }
}

export async function getWebPushStatus(): Promise<{ supported: boolean; permission: NotificationPermission | 'na'; subscribed: boolean }> {
  try {
    const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    if (!supported) return { supported: false, permission: 'na', subscribed: false };
    const permission = Notification.permission;
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    return { supported: true, permission, subscribed: !!sub };
  } catch {
    return { supported: false, permission: 'na', subscribed: false };
  }
}


